import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RewardAdDialog } from "@/components/RewardAd";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Coins, DollarSign, ArrowRightLeft, Banknote, ShieldCheck, Play, ArrowDownToLine, Loader2, ShoppingCart, Sparkles, Check } from "lucide-react";

export default function Wallet() {
  const { user, applyUser } = useAuth();
  const [w, setW] = useState(null);
  const [txns, setTxns] = useState([]);
  const [cashouts, setCashouts] = useState([]);
  const [ad, setAd] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [cashoutOpen, setCashoutOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [convertCoins, setConvertCoins] = useState("");
  const [cashoutAmt, setCashoutAmt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, b, c] = await Promise.all([api.get("/wallet"), api.get("/transactions"), api.get("/cashouts/mine")]);
    setW(a.data); setTxns(b.data); setCashouts(c.data);
  };
  useEffect(() => { load(); }, []);

  const watchAd = async () => {
    setAd(false);
    try {
      const { data } = await api.post("/rewards/ad", { placement: "generic" });
      applyUser(data.user);
      toast.success(`+${data.reward.coins} coins${data.reward.cash ? `, +$${data.reward.cash}` : ""}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const convert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/convert", { coins: parseInt(convertCoins) });
      applyUser(data.user); toast.success(`Converted to $${data.cash}`); setConvertOpen(false); setConvertCoins(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const cashout = async () => {
    setBusy(true);
    try {
      await api.post("/wallet/cashout", { amount: parseFloat(cashoutAmt) });
      toast.success("Cash-out requested (pending approval)"); setCashoutOpen(false); setCashoutAmt("");
      const me = await api.get("/auth/me"); applyUser(me.data); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!w || !user) return <p className="text-center text-muted-foreground py-8">Loading wallet…</p>;
  const verifiedPct = Math.min(100, Math.round((w.verified_cleanups / w.verified_required) * 100));
  const canCashout = w.verified_cleanups >= w.verified_required;

  return (
    <div className="space-y-5">
      {ad && <RewardAdDialog open onClose={() => setAd(false)} onComplete={watchAd} label="Earn Coins" />}
      <div className="flex items-center gap-2"><Banknote className="w-6 h-6 text-jixxeo-green" /><h2 className="font-head font-extrabold text-xl">Jixxeo Wallet</h2></div>

      <div className="grid grid-cols-2 gap-3">
        <div className="glass rounded-3xl p-5 border border-jixxeo-gold/30">
          <Coins className="w-6 h-6 text-jixxeo-gold" />
          <p className="font-display text-3xl text-jixxeo-gold mt-2 text-glow-gold" data-testid="coin-balance">{user.coins}</p>
          <p className="text-xs uppercase text-muted-foreground">Jixxeo Coins</p>
        </div>
        <div className="glass rounded-3xl p-5 border border-jixxeo-green/30">
          <DollarSign className="w-6 h-6 text-jixxeo-green" />
          <p className="font-display text-3xl text-jixxeo-green mt-2 text-glow-green" data-testid="cash-balance">${user.cash_balance?.toFixed(2)}</p>
          <p className="text-xs uppercase text-muted-foreground">Cash Balance</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Button onClick={() => setAd(true)} data-testid="earn-ad-btn" className="rounded-full bg-jixxeo-blue text-white font-bold hover:opacity-90 neon-blue h-11"><Play className="w-4 h-4 mr-1" />Earn</Button>
        <Button onClick={() => setConvertOpen(true)} data-testid="convert-btn" variant="ghost" className="rounded-full glass border border-white/10 h-11"><ArrowRightLeft className="w-4 h-4 mr-1" />Convert</Button>
        <Button onClick={() => canCashout ? setCashoutOpen(true) : toast.error(`Complete ${w.verified_required} verified cleanups first`)} data-testid="cashout-btn" className="rounded-full bg-jixxeo-green text-black font-bold hover:bg-jixxeo-greenDark h-11"><ArrowDownToLine className="w-4 h-4 mr-1" />Cash Out</Button>
      </div>

      <Button onClick={() => setBuyOpen(true)} data-testid="buy-coins-btn" className="w-full rounded-full bg-jixxeo-gold text-black font-bold hover:bg-yellow-400 neon-gold h-12">
        <ShoppingCart className="w-5 h-5 mr-2" />Buy Jixxeo Coins
      </Button>

      {/* Anti-fraud gate */}
      <div className="glass rounded-2xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-jixxeo-green" />Verified cleanups to cash out</span>
          <span className="text-xs text-muted-foreground" data-testid="verified-progress">{w.verified_cleanups}/{w.verified_required}</span>
        </div>
        <Progress value={verifiedPct} className="h-2 bg-white/10" />
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <span>Daily cash cap: ${w.cash_earned_today.toFixed(2)}/${w.daily_cash_cap.toFixed(2)}</span>
          <span>Min ${w.cashout_min} · Fee ${w.cashout_fee}</span>
        </div>
      </div>

      {cashouts.length > 0 && (
        <div>
          <h3 className="font-head font-bold text-sm mb-2 text-muted-foreground uppercase tracking-wider">Cash-out Requests</h3>
          <div className="space-y-2">
            {cashouts.map((c) => (
              <div key={c.id} className="glass rounded-xl p-3 flex items-center justify-between text-sm" data-testid={`cashout-${c.id}`}>
                <span>${c.amount.toFixed(2)} <span className="text-xs text-muted-foreground">(fee ${c.fee})</span></span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.status === "approved" ? "bg-jixxeo-green/20 text-jixxeo-green" : c.status === "denied" ? "bg-jixxeo-danger/20 text-jixxeo-danger" : "bg-jixxeo-gold/20 text-jixxeo-gold"}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="font-head font-bold text-sm mb-2 text-muted-foreground uppercase tracking-wider">Transaction History</h3>
        {txns.length === 0 ? (
          <div className="glass rounded-2xl p-6 text-center text-muted-foreground text-sm">No transactions yet. Start scanning!</div>
        ) : (
          <div className="space-y-1.5">
            {txns.map((t) => (
              <div key={t.id} className="glass rounded-xl p-3 flex items-center justify-between" data-testid={`txn-${t.id}`}>
                <div className="min-w-0"><p className="text-sm font-medium truncate capitalize">{t.note || t.kind.replace(/_/g, " ")}</p>
                  <p className="text-[10px] text-muted-foreground">{new Date(t.created_at).toLocaleString()}</p></div>
                <div className="text-right shrink-0">
                  {t.coins !== 0 && <p className={`text-sm font-bold ${t.coins > 0 ? "text-jixxeo-gold" : "text-muted-foreground"}`}>{t.coins > 0 ? "+" : ""}{t.coins} 🪙</p>}
                  {t.cash !== 0 && <p className={`text-xs font-bold ${t.cash > 0 ? "text-jixxeo-green" : "text-jixxeo-danger"}`}>{t.cash > 0 ? "+" : ""}${t.cash.toFixed(2)}</p>}
                  {t.xp > 0 && <p className="text-[10px] text-jixxeo-blue">+{t.xp} XP</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent className="glass border-white/10">
          <DialogHeader><DialogTitle>Convert Coins to Cash</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Rate: {Math.round(1 / w.coin_to_cash)} coins = $1.00</p>
          <Input type="number" placeholder="Coins to convert" value={convertCoins} onChange={(e) => setConvertCoins(e.target.value)} data-testid="convert-input" className="bg-white/5 border-white/10 rounded-xl h-12" />
          {convertCoins && <p className="text-sm text-jixxeo-green">= ${(parseInt(convertCoins || 0) * w.coin_to_cash).toFixed(2)}</p>}
          <Button onClick={convert} disabled={busy} data-testid="convert-confirm" className="rounded-full bg-jixxeo-green text-black font-bold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Convert"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={cashoutOpen} onOpenChange={setCashoutOpen}>
        <DialogContent className="glass border-white/10">
          <DialogHeader><DialogTitle>Cash Out</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Minimum ${w.cashout_min} · Processing fee ${w.cashout_fee}. Money movement is simulated in this build.</p>
          <Input type="number" placeholder={`Amount (min $${w.cashout_min})`} value={cashoutAmt} onChange={(e) => setCashoutAmt(e.target.value)} data-testid="cashout-input" className="bg-white/5 border-white/10 rounded-xl h-12" />
          {cashoutAmt && <p className="text-sm text-muted-foreground">Total deducted: ${(parseFloat(cashoutAmt || 0) + w.cashout_fee).toFixed(2)}</p>}
          <Button onClick={cashout} disabled={busy} data-testid="cashout-confirm" className="rounded-full bg-jixxeo-green text-black font-bold">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Cash-out"}</Button>
        </DialogContent>
      </Dialog>

      <BuyCoinsDialog open={buyOpen} setOpen={setBuyOpen} applyUser={applyUser} reload={load} />
    </div>
  );
}

function BuyCoinsDialog({ open, setOpen, applyUser, reload }) {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stage, setStage] = useState("pick"); // pick | pay | done
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { api.get("/coins/packages").then((r) => setPackages(r.data)).catch(() => {}); setStage("pick"); setSelected(null); }
  }, [open]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/coins/purchase", { package_id: selected.id });
      applyUser(data.user);
      setStage("done");
      toast.success(`+${data.coins_added} coins added!`);
      reload?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="glass border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShoppingCart className="w-5 h-5 text-jixxeo-gold" />Buy Jixxeo Coins</DialogTitle></DialogHeader>

        {stage === "pick" && (
          <div className="space-y-2">
            {packages.map((p) => (
              <button key={p.id} onClick={() => { setSelected(p); setStage("pay"); }} data-testid={`coin-pack-${p.id}`}
                className={`w-full glass rounded-2xl p-4 flex items-center justify-between border ${p.popular ? "border-jixxeo-gold neon-gold" : "border-white/10"} hover:scale-[1.01] transition-transform`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-jixxeo-gold/15 flex items-center justify-center"><Coins className="w-6 h-6 text-jixxeo-gold" /></div>
                  <div className="text-left">
                    <p className="font-display text-lg text-jixxeo-gold">{p.coins + p.bonus}</p>
                    <p className="text-[11px] text-muted-foreground">{p.label}{p.bonus > 0 ? ` · +${p.bonus} bonus` : ""}</p>
                  </div>
                </div>
                <div className="text-right">
                  {p.popular && <span className="text-[9px] bg-jixxeo-gold text-black font-bold px-1.5 py-0.5 rounded-full">POPULAR</span>}
                  <p className="font-bold text-jixxeo-green mt-1">${p.price.toFixed(2)}</p>
                </div>
              </button>
            ))}
            <p className="text-[11px] text-center text-muted-foreground pt-1">Payments are simulated in this build — no card is charged.</p>
          </div>
        )}

        {stage === "pay" && selected && (
          <div className="space-y-4 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-jixxeo-gold/15 flex items-center justify-center"><Coins className="w-9 h-9 text-jixxeo-gold" /></div>
            <div>
              <p className="font-display text-2xl text-jixxeo-gold">{selected.coins + selected.bonus} coins</p>
              <p className="text-sm text-muted-foreground">{selected.label} pack · ${selected.price.toFixed(2)}</p>
            </div>
            <div className="glass rounded-xl p-3 text-left text-xs text-muted-foreground">
              <p className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-jixxeo-blue" />Simulated secure checkout</p>
              <p className="mt-1">Tap confirm to complete your purchase instantly.</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setStage("pick")} variant="ghost" className="flex-1 rounded-full glass border border-white/10">Back</Button>
              <Button onClick={pay} disabled={busy} data-testid="coin-purchase-confirm" className="flex-1 rounded-full bg-jixxeo-gold text-black font-bold hover:bg-yellow-400">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay $${selected.price.toFixed(2)}`}</Button>
            </div>
          </div>
        )}

        {stage === "done" && selected && (
          <div className="space-y-4 text-center py-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-jixxeo-green/20 flex items-center justify-center pulse-glow"><Check className="w-9 h-9 text-jixxeo-green" /></div>
            <p className="font-display text-xl text-jixxeo-green">Purchase Complete!</p>
            <p className="text-sm text-muted-foreground">{selected.coins + selected.bonus} Jixxeo Coins added to your wallet.</p>
            <Button onClick={() => setOpen(false)} data-testid="coin-purchase-done" className="w-full rounded-full bg-jixxeo-green text-black font-bold">Done</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
